import { Box, Button, Text } from '@nimbus-ds/components';
import { AgentAvatarIcon } from 'commons/components/AgentChat/img';
import { useTranslationCommon } from 'commons/hooks';
import { useStoreName } from 'domains/Auth/hooks';
import { useAgentChatContext } from '../../contexts';
import { trackAgentChatSuggestionUsed } from '../../tracking';

interface ChatWelcomeScreenProps {
  onQuickAction: (action: string) => void;
}

/**
 * ChatWelcomeScreen displays the initial welcome message and quick action buttons
 * when no conversation has started yet.
 */
function ChatWelcomeScreen({
  onQuickAction,
}: ChatWelcomeScreenProps): JSX.Element {
  const t = useTranslationCommon('ai.agent');
  const name = useStoreName();
  const { isLoading, sessionId } = useAgentChatContext();

  const quickActions = [
    {
      key: 'generateDescription',
      label: t('generateDescription'),
    },
    {
      key: 'configureWeightsAndDimensions',
      label: t('configureWeightsAndDimensions'),
    },
    {
      key: 'getCategories',
      label: t('getCategories'),
    },
  ];

  return (
    <Box display="flex" flexDirection="column" flex="1" padding="4">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        flex="1"
        gap="4"
      >
        <AgentAvatarIcon width={40} height={40} />
        <Box
          textAlign="center"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Text
            fontSize="highlight"
            fontWeight="bold"
            color="neutral-textHigh"
            textAlign="center"
            lineHeight="highlight"
          >
            {t('greeting', { name })}
          </Text>
          <Text
            fontSize="base"
            color="neutral-textLow"
            textAlign="center"
            lineHeight="base"
            fontWeight="regular"
          >
            {t('introduction')}
          </Text>
          <Text
            color="ai-generative"
            fontSize="base"
            fontWeight="medium"
            textAlign="center"
            lineHeight="highlight"
          >
            {t('howCanIHelp')}
          </Text>
        </Box>
      </Box>

      <Box display="flex" flexDirection="column" gap="2" width="100%">
        {quickActions.map((action) => (
          <Button
            key={action.key}
            appearance="neutral"
            onClick={() => {
              trackAgentChatSuggestionUsed({
                suggestion: action.label,
                sessionId,
              });
              onQuickAction(action.label);
            }}
            disabled={isLoading}
            size="small"
          >
            {action.label}
          </Button>
        ))}
      </Box>
    </Box>
  );
}

export default ChatWelcomeScreen;
